import { useState } from 'react';
import { ApiClient } from '../../api/client';
import WebApp from '@twa-dev/sdk';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { escapeMarkdownV2 } from '../../utils/markdown';
import { UserInfo } from '../../types';

interface AccessRequestProps {
  creationId: string;
  userInfo: UserInfo | null;
}

export function AccessRequest({ creationId, userInfo }: AccessRequestProps) {
  const [isRequesting, setIsRequesting] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const handleRequestAccess = async () => {
    if (isRequesting || !userInfo) return;
    
    try {
      setIsRequesting(true);
      
      const botUsername = import.meta.env.VITE_BOT_USERNAME;
      const miniAppName = import.meta.env.VITE_MINI_APP_NAME;
      const shareUrl = `https://t.me/${botUsername}/${miniAppName}?startapp=creation_${creationId}`;
      
      let requesterName = t('accessRequest.defaultRequesterName');
      if (userInfo.first_name && userInfo.last_name) {
        requesterName = `${userInfo.first_name} ${userInfo.last_name}`;
      } else if (userInfo.first_name) {
        requesterName = userInfo.first_name;
      } else if (userInfo.username) {
        requesterName = `@${userInfo.username}`;
      }

      const standardMessage = t('accessRequest.requestMessageToOwner', { name: requesterName });
      const escapedMessage = escapeMarkdownV2(standardMessage);

      await ApiClient.requestCreationAccess(creationId, {
        message: escapedMessage,
        button_text: t('accessRequest.viewCreationButton'),
        button_url: shareUrl,
        parse_mode: 'MarkdownV2'
      });
      
      WebApp.showPopup({
        title: t('accessRequest.successPopupTitle'),
        message: t('accessRequest.successPopupMessage'),
        buttons: [{ type: 'ok' }]
      });
    } catch (error) {
      console.error('Failed to request access:', error);
      WebApp.showPopup({
        title: t('errors.errorTitle'),
        message: t('accessRequest.errorPopupMessage'),
        buttons: [{ type: 'ok' }]
      });
    } finally {
      setIsRequesting(false);
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };
  
  return (
    <div className="min-h-screen bg-theme-bg flex flex-col items-center justify-center p-4">
      <div className="bg-surface-modal dark:bg-surface-modal-dark rounded-xl p-6 shadow-lg max-w-md w-full transform transition-all animate-fade-in">
        <div className="text-center mb-8">
          <div className="bg-critical-soft dark:bg-critical-soft-dark w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-4xl text-critical">lock</span>
          </div>
          <h1 className="text-2xl font-bold text-content-strong">{t('accessRequest.header')}</h1>
          <p className="text-content-secondary mt-2">
            {t('accessRequest.description')}
          </p>
        </div>
        
        <button
          onClick={handleRequestAccess}
          disabled={isRequesting}
          className="w-full py-3 px-4 bg-button-primary text-on-button-primary font-medium rounded-lg hover:bg-button-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {isRequesting ? (
            <>
              <span className="animate-spin h-4 w-4 border-2 border-on-button-primary border-t-transparent rounded-full"></span>
              <span>{t('accessRequest.sendingButton')}</span>
            </>
          ) : (
            <>
              <span className="material-symbols-outlined">send</span>
              <span>{t('accessRequest.requestButton')}</span>
            </>
          )}
        </button>

        <button
          onClick={handleGoHome}
          className="w-full mt-3 py-3 px-4 bg-button-neutral text-on-button-neutral font-medium rounded-lg hover:bg-button-neutral-hover transition-colors flex items-center justify-center gap-2"
        >
          <span className="material-symbols-outlined">home</span>
          <span>{t('accessRequest.goHomeButton')}</span>
        </button>
      </div>
    </div>
  );
} 